// src/redux/slices/cashRegister.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getCashRegisters,
  getCashRegister,
  getCurrentSession,
  openSession,
  closeSession,
  getSessionTransactions,
  createTransaction
} from '../../api/server/cashRegister';
import { CashRegister, CashRegisterSession, Transaction } from '../../types/cashRegister';

interface CashRegisterState {
  registers: CashRegister[];
  currentRegister: CashRegister | null;
  currentSession: CashRegisterSession | null;
  transactions: Transaction[];
  denominations: Array<{
    value: number;
    label: string;
    count: number;
  }>;
  loading: boolean;
  error: string | null;
}

const initialState: CashRegisterState = {
  registers: [],
  currentRegister: null,
  currentSession: null,
  transactions: [],
  denominations: [],
  loading: false,
  error: null,
};

// Thunks
export const fetchCashRegisters = createAsyncThunk(
  'cashRegister/fetchAll',
  async (idUser: string, { rejectWithValue }) => {
    try {
      const response = await getCashRegisters(idUser);
      return response.data.filter((register: CashRegister | null) => register !== null).map((register: CashRegister) => {
        register.totalTransactions = (register.sessions??[]).reduce((prev, curr) => prev + curr.transactions.length, 0)
        register.totalAmount = (register.sessions??[]).reduce((prev, curr) => prev +
          curr.transactions.reduce((prevTrans, currTrans) => prevTrans + currTrans.total,0), 0)
        return register
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Erreur lors du chargement des caisses');
    }
  }
);

export const fetchCashRegister = createAsyncThunk(
  'cashRegister/fetchOne',
  async ({ idUser, cashRegisterId }: { idUser: string; cashRegisterId: string }, { rejectWithValue }) => {
    try {
      const response = await getCashRegister(idUser, cashRegisterId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Erreur lors du chargement de la caisse');
    }
  }
);

export const fetchCurrentSession = createAsyncThunk(
  'cashRegister/fetchCurrentSession',
  async ({ idUser, cashRegisterId }: { idUser: string; cashRegisterId?: string }, { rejectWithValue }) => {
    try {
      const response = await getCurrentSession(idUser, cashRegisterId);
      // Gérer le cas où l'API retourne une structure d'erreur
      if (response.error) {
        console.warn('[fetchCurrentSession] API returned error:', response.error);
        return null; // Session non trouvée est un cas normal
      }
      return response.data;
    } catch (error) {
      console.error('[fetchCurrentSession] Error:', error);
      // Ne pas rejeter avec une erreur pour les cas normaux (session non trouvée)
      return null;
    }
  }
);

export const startSession = createAsyncThunk(
  'cashRegister/startSession',
  async (
    { idUser, cashRegisterId, openingAmount, openingDenominations, notes }:
    { idUser: string; cashRegisterId?: string; openingAmount: number; openingDenominations?: any[]; notes?: string },
    { rejectWithValue }
  ) => {
    console.log('[startSession] Calling API with:', { idUser, cashRegisterId, openingAmount });
    try {
      const response = await openSession(idUser, cashRegisterId, { openingAmount, openingDenominations, notes });
      console.log('[startSession] API response:', response);
      return response.data;
    } catch (error: any) {
      console.error('[startSession] API error:', error);
      return rejectWithValue(error.message || "Erreur lors de l'ouverture de la session");
    }
  }
);

export const endSession = createAsyncThunk(
  'cashRegister/endSession',
  async (
    { idUser, cashRegisterId, closingAmount, closingDenominations, notes }:
    { idUser: string; cashRegisterId?: string; closingAmount: number; closingDenominations?: any[]; notes?: string },
    { rejectWithValue }
  ) => {
    console.log('[endSession] Calling API with:', { idUser, cashRegisterId, closingAmount });
    try {
      const response = await closeSession(idUser, cashRegisterId, { closingAmount, closingDenominations, notes });
      console.log('[endSession] API response:', response);
      return response.data;
    } catch (error: any) {
      console.error('[endSession] API error:', error);
      return rejectWithValue(error.message || 'Erreur lors de la fermeture de la session');
    }
  }
);

export const fetchTransactions = createAsyncThunk(
  'cashRegister/fetchTransactions',
  async (
    { idUser, cashRegisterId, sessionId }:
    { idUser: string; cashRegisterId?: string; sessionId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await getSessionTransactions(idUser, cashRegisterId, sessionId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Erreur lors du chargement des transactions');
    }
  }
);

export const addTransaction = createAsyncThunk(
  'cashRegister/addTransaction',
  async (
    { idUser, cashRegisterId, sessionId, transaction }:
    {
      idUser: string;
      cashRegisterId?: string;
      sessionId: string;
      transaction: {
        type: 'sale' | 'refund' | 'expense' | 'deposit' | 'withdrawal';
        amount: number;
        paymentMethod: string;
        reference?: string;
        notes?: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const data = {
        ...transaction,
        cashRegister: cashRegisterId,
        cashRegisterSession: sessionId,
        amountPaid: transaction.amount, // Par défaut on considère tout payé
        payments: [{
          method: transaction.paymentMethod,
          amount: transaction.amount,
          status: 'completed'
        }],
        items: [] // On pourrait ajouter un item générique ici si nécessaire
      };
      const response = await createTransaction(idUser, cashRegisterId, data as any);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erreur lors de l'ajout de la transaction");
    }
  }
);

const cashRegisterSlice = createSlice({
  name: 'cashRegister',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
    setDenominations: (state, {payload}) => {
      console.log('setDenominations', payload)
      state.denominations = payload
    },
    incrementDenomination: (state, {payload}) => {
      const {value, label} = payload
      const denominationFound = state.denominations.find(d => d.value === value)
      if (!denominationFound) state.denominations.push({value, label, count: 1})
      else denominationFound.count += 1
    },
    decrementDenomination: (state, {payload}) => {
      const {value} = payload
      const denominationFound = state.denominations.find(d => d.value === value)
      if (denominationFound) denominationFound.count -= 1
    },
    resetDenominations: (state) => {
      state.denominations = []
    },
    updateSessionFromWebSocket: (state, { payload }) => {
      // Met à jour la session en cours avec les données reçues du WebSocket
      console.log('[cashRegisterSlice] updateSessionFromWebSocket:', {
        payloadId: payload?.id,
        currentSessionId: state.currentSession?.id,
        payloadCurrentAmount: payload?.currentAmount,
        payloadTotalSales: payload?.totalSales,
      });

      if (state.currentSession && payload.id === state.currentSession.id) {
        state.currentSession = {
          ...state.currentSession,
          ...payload,
          // Préserver les transactions existantes si elles ne sont pas dans le payload
          transactions: payload.transactions || state.currentSession.transactions,
        };
        console.log('[cashRegisterSlice] Session updated in Redux:', {
          currentAmount: state.currentSession?.currentAmount,
          totalSales: state.currentSession?.totalSales,
        });
      }
    }
  },
  extraReducers: (builder) => {
    // Gestion des états de chargement et d'erreur
    const handlePending = (state: CashRegisterState) => {
      state.loading = true;
      state.error = null;
    };

    const handleRejected = (state: CashRegisterState, action: any) => {
      state.loading = false;
      state.error = action.payload || 'Une erreur est survenue';
    };

    // fetchCashRegisters
    builder
      .addCase(fetchCashRegisters.pending, handlePending)
      .addCase(fetchCashRegisters.fulfilled, (state, action) => {
        state.loading = false;
        state.registers = action.payload;
      })
      .addCase(fetchCashRegisters.rejected, handleRejected);

    // fetchCashRegister
    builder
      .addCase(fetchCashRegister.pending, handlePending)
      .addCase(fetchCashRegister.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRegister = action.payload;
      })
      .addCase(fetchCashRegister.rejected, handleRejected);

    // fetchCurrentSession
    builder
      .addCase(fetchCurrentSession.pending, handlePending)
      .addCase(fetchCurrentSession.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload;
      })
      .addCase(fetchCurrentSession.rejected, (state, action) => {
        // Si la session n'existe pas ou erreur serveur, on la met à null sans erreur
        console.error('[fetchCurrentSession.rejected] Error:', action.payload);
        state.loading = false;
        state.currentSession = null;
        state.error = null; // Ne pas propager l'erreur pour éviter de bloquer l'UI
      });

    // startSession
    builder
      .addCase(startSession.pending, handlePending)
      .addCase(startSession.fulfilled, (state, action) => {
        console.log('[startSession.fulfilled] Payload:', action.payload);
        state.loading = false;
        state.currentSession = action.payload;
        if (state.currentRegister) {
          state.currentRegister.status = 'open';
        }
      })
      .addCase(startSession.rejected, (state, action) => {
        console.error('[startSession.rejected] Error:', action.payload);
        handleRejected(state, action);
      });

    // endSession
    builder
      .addCase(endSession.pending, handlePending)
      .addCase(endSession.fulfilled, (state, action) => {
        console.log('[endSession.fulfilled] Success, payload:', action.payload);
        state.loading = false;
        state.currentSession = null;
        if (state.currentRegister) {
          state.currentRegister.status = 'closed';
        }
      })
      .addCase(endSession.rejected, (state, action) => {
        console.error('[endSession.rejected] Error:', action.payload);
        handleRejected(state, action);
      });

    // fetchTransactions
    builder
      .addCase(fetchTransactions.pending, handlePending)
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
        if (state.currentSession) {
          state.currentSession.transactions = action.payload.map((t: any) => t.id);
        }
      })
      .addCase(fetchTransactions.rejected, handleRejected);

    // addTransaction
    builder
      .addCase(addTransaction.pending, handlePending)
      .addCase(addTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions.unshift(action.payload);
        if (state.currentSession) {
          if (!state.currentSession.transactions) {
            state.currentSession.transactions = [];
          }
          state.currentSession.transactions.push(action.payload.id);

          // Mettre à jour les totaux de la session localement
          if (action.payload.type === 'deposit' || action.payload.type === 'sale') {
            state.currentSession.totalSales = (state.currentSession.totalSales || 0) + action.payload.amount;
          } else if (action.payload.type === 'withdrawal' || action.payload.type === 'refund') {
            state.currentSession.totalRefunds = (state.currentSession.totalRefunds || 0) + action.payload.amount;
          }
        }
      })
      .addCase(addTransaction.rejected, handleRejected);
  },
});

export const { clearError, resetState, setDenominations, incrementDenomination, decrementDenomination, resetDenominations, updateSessionFromWebSocket } = cashRegisterSlice.actions;
export default cashRegisterSlice.reducer;
